import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
    Search, PlayCircle, CreditCard, Package, TrendingUp, Cpu, 
    ShieldCheck, HelpCircle, ArrowRight, MessageSquare, ArrowLeft, BookOpen, Clock 
} from 'lucide-react';
import SEO from '../../components/SEO';

const HelpCenterPage = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedArticle, setSelectedArticle] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [selectedCategory, selectedArticle]);

    const categories = [
        {
            title: "Getting Started",
            desc: "Setup your business profile, add sales executives, and manage team access controls.",
            icon: PlayCircle,
            color: "text-emerald-600 bg-emerald-50"
        },
        {
            title: "POS & Billing",
            desc: "Learn to manage walk-in sales, credit billing, and GST tax calculations.",
            icon: CreditCard,
            color: "text-blue-600 bg-blue-50"
        },
        {
            title: "Inventory Management",
            desc: "Track stock counts, batches, category definitions, and low-stock alerts.",
            icon: Package,
            color: "text-amber-600 bg-amber-50"
        },
        {
            title: "Reports & GST",
            desc: "Export sales ledgers, track HSN records, and compile GSTR compliance files.",
            icon: TrendingUp,
            color: "text-indigo-600 bg-indigo-50"
        },
        {
            title: "Hardware Setup",
            desc: "Step-by-step guides to connect barcode scanners and thermal printers.",
            icon: Cpu,
            color: "text-teal-600 bg-teal-50"
        },
        {
            title: "Account & Subscriptions",
            desc: "Manage subscription plans, check active credits, and configure profile parameters.",
            icon: ShieldCheck,
            color: "text-rose-600 bg-rose-50"
        }
    ];

    const articlesData = {
        "Getting Started": [
            {
                title: "How to set up your business profile",
                readTime: "4 min read",
                excerpt: "Learn how to configure your business address, contact, GSTIN, currency, and brand logo.",
                content: `
                    <p class="mb-4">Setting up your business profile is the first step to configure your invoices, tax headers, and thermal receipts correctly. Follow these simple steps:</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">1. Access Company Settings</h3>
                    <p class="mb-4">Log in to your Owner portal, open the side navigation menu, and navigate to the <strong>Company Profile</strong> page under settings.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">2. Enter Business Information</h3>
                    <p class="mb-4">Input your legal business name, registered address, telephone number, and customer support email. This data is dynamically compiled and printed onto receipt headers.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">3. Add GST details</h3>
                    <p class="mb-4">If your enterprise is GST registered, check the GST toggle, input your 15-digit <strong>GSTIN</strong>, and select your state jurisdiction. The system requires this to determine if CGST + SGST (within state) or IGST (out of state) should apply to transactions.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">4. Upload Logo</h3>
                    <p class="mb-4">Select and upload a PNG or JPG logo. The logo is automatically optimized to print clearly on standard receipts and A4 invoices.</p>
                `
            },
            {
                title: "Managing employee roles and permissions",
                readTime: "5 min read",
                excerpt: "Assign team access permissions to staff, managers, and sales executives.",
                content: `
                    <p class="mb-4">Gio Billing implements strict access control layers to secure audit records and settings from unauthorized entry. Learn roles here:</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">1. Preset Access Roles</h3>
                    <ul class="list-disc pl-5 mb-4 space-y-2">
                        <li><strong>Owner:</strong> Full operational controls, billing settings, subscriptions, and financial report exports.</li>
                        <li><strong>Manager:</strong> Adjusts inventory count, logs inward stock, and overrides bill pricing. Restricted from deleting system configurations.</li>
                        <li><strong>Sales Executive:</strong> Access is limited exclusively to the POS Billing screen, transaction logs, and profile settings.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">2. How to invite staff</h3>
                    <p class="mb-4">Open the <strong>Team Access</strong> portal, click the add button, enter your staff member's email, phone, and assign their operational role. Once invited, they can set up their login passcode.</p>
                `
            }
        ],
        "POS & Billing": [
            {
                title: "Making your first walk-in sale",
                readTime: "3 min read",
                excerpt: "Quick-start guide to using the POS Billing screen, barcode scanning, and accepting payments.",
                content: `
                    <p class="mb-4">The POS Billing interface is optimized for rapid checkout operations. Follow this step-by-step workflow to complete your first invoice:</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">1. Open the POS screen</h3>
                    <p class="mb-4">Navigate to the <strong>POS Billing</strong> menu. The search focus is automatically active in the product input bar.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">2. Add Items to cart</h3>
                    <p class="mb-4">Use a connected barcode scanner to scan the product label, or type the name/code in the search input and click enter to append to the active billing list.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">3. Process Payment & Checkout</h3>
                    <p class="mb-4">Review quantities and select payment type (Cash, Card, UPI, or Credit/Ledger). Input received cash amount to compute the exact change. Click <strong>Complete Checkout</strong> to log the transaction and emit the print receipt.</p>
                `
            },
            {
                title: "Understanding GST invoicing details",
                readTime: "4 min read",
                excerpt: "How tax rates are calculated automatically based on customer registry locations.",
                content: `
                    <p class="mb-4">Gio Billing computes compliant GST tax details in real-time. Here is how the engine behaves:</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">1. Intrastate Sales (Local)</h3>
                    <p class="mb-4">If the customer resides in the same state as your company profile, the system divides the product HSN tax slab equally into <strong>CGST (Central GST)</strong> and <strong>SGST (State GST)</strong>.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">2. Interstate Sales (Out-of-State)</h3>
                    <p class="mb-4">If the customer profile is located in another state, the system applies the entire tax percentage to <strong>IGST (Integrated GST)</strong>.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">3. Compliance Records</h3>
                    <p class="mb-4">All tax calculations are summarized with HSN/SAC codes automatically, making monthly GSTR reports preparation instantaneous.</p>
                `
            }
        ],
        "Inventory Management": [
            {
                title: "Adding and editing products in inventory",
                readTime: "4 min read",
                excerpt: "How to create products, enter barcode numbers, set purchase/sales prices, and specify HSN codes.",
                content: `
                    <p class="mb-4">Maintaining an updated product catalogue ensures accurate margins and rapid checkouts. Follow these instructions:</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">1. Navigate to Inventory</h3>
                    <p class="mb-4">Open the side navigation, click <strong>Inventory</strong>, and select the <strong>Add Product</strong> button at the top-right.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">2. Fill Product Details</h3>
                    <p class="mb-4">Enter the product title, select a category, and scan or type the barcode code (EAN/UPC). Specify your <strong>Purchase Price</strong> (buying cost) and <strong>Sale Price</strong> (billing rate) to track gross profit margins.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">3. Set HSN & GST codes</h3>
                    <p class="mb-4">Select the correct HSN tax code for the product class and declare the tax rate (e.g. 5%, 12%, 18%). Save changes. The product is now ready for POS searches.</p>
                `
            },
            {
                title: "Uploading products in bulk using Excel/CSV",
                readTime: "5 min read",
                excerpt: "Download the template sheet, align your product parameters, and upload for instant bulk inventory creation.",
                content: `
                    <p class="mb-4">If you are migrating from legacy platforms, you can bulk-load your stock records in minutes:</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">1. Download Excel Template</h3>
                    <p class="mb-4">Go to the Inventory dashboard, click the bulk options dropdown, and choose <strong>Download Import Template</strong>.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">2. Format Your Product Rows</h3>
                    <p class="mb-4">Open the template Excel file. Populate the columns (Name, Barcode, Sale Price, Purchase Price, HSN, Tax Rate, Stock Count). Avoid renaming column headers, as the importer validates the exact format.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">3. Upload File</h3>
                    <p class="mb-4">Drag and drop your spreadsheet into the bulk uploader window and click <strong>Validate & Import</strong>. Errors (if any) will be highlighted with column references.</p>
                `
            }
        ],
        "Reports & GST": [
            {
                title: "Generating daily and monthly sales summaries",
                readTime: "3 min read",
                excerpt: "How to view sales dashboards, filter by payment type, and export custom reports.",
                content: `
                    <p class="mb-4">Assess business health easily using our reporting modules:</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">1. Access Reports Dashboard</h3>
                    <p class="mb-4">Navigate to the <strong>Reports</strong> page in your dashboard drawer.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">2. Filter Parameters</h3>
                    <p class="mb-4">Specify the start date, end date, and select payment filters (e.g., UPI payments, credit ledger accounts) or product category breakdowns.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">3. Export data</h3>
                    <p class="mb-4">Click <strong>Export PDF</strong> or <strong>Export Excel</strong> to download files for accountant validation or auditing.</p>
                `
            },
            {
                title: "Understanding HSN/SAC summary reports",
                readTime: "4 min read",
                excerpt: "Export summaries of taxes grouped by HSN/SAC codes for GSTR filing preparation.",
                content: `
                    <p class="mb-4">Tax filings demand clean grouping by HSN (Harmonized System of Nomenclature) numbers. Gio Billing simplifies this:</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">1. HSN Tax Table</h3>
                    <p class="mb-4">Under Reports, choose <strong>GST / Tax Summaries</strong>. The platform presents a consolidated view showing HSN Codes, taxable value, and CGST/SGST/IGST breakdown.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">2. Auto GSTR Extraction</h3>
                    <p class="mb-4">Download the GSTR-1 formatted spreadsheet. This matches structural requirements directly, avoiding double data entry.</p>
                `
            }
        ],
        "Hardware Setup": [
            {
                title: "Connecting USB & Bluetooth barcode scanners",
                readTime: "3 min read",
                excerpt: "Simple steps to test and integrate barcode scanners with your POS terminal.",
                content: `
                    <p class="mb-4">Our billing system is fully scanner-agnostic. Setting up scanning hardware requires zero extra configurations:</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">1. Hardware Installation</h3>
                    <p class="mb-4">Connect your scanner via USB cable or link the device via Bluetooth settings on your Windows terminal.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">2. Configuration Test</h3>
                    <p class="mb-4">Barcode scanners behave as standard keyboard inputs. Open notepad and scan a code. If the numbers appear followed by a line break, the device is configured correctly.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">3. POS Search Integration</h3>
                    <p class="mb-4">Open the POS Billing module. Scanner focus will automatically route scanned codes to identify stock rows.</p>
                `
            },
            {
                title: "Configuring thermal receipt printers",
                readTime: "4 min read",
                excerpt: "Windows printer driver installation guidelines, paper size configuration, and page margin adjustments.",
                content: `
                    <p class="mb-4">Setup your thermal receipt printer for high speed receipt emission:</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">1. Driver Installation</h3>
                    <p class="mb-4">Connect printer to device. Download the official hardware vendor driver, install it, and execute a diagnostic print test.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">2. Page sizing</h3>
                    <p class="mb-4">In Windows Control Panel under Devices & Printers, open Printer Properties. Modify the paper dimensions to match receipt roll size: <strong>80mm (3-inch)</strong> or <strong>58mm (2-inch)</strong>.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">3. System Alignment</h3>
                    <p class="mb-4">In POS Print Settings inside the application, match print outputs to your printer name to bypass manual print dialog boxes.</p>
                `
            }
        ],
        "Account & Subscriptions": [
            {
                title: "Upgrading your plan and purchasing credits",
                readTime: "4 min read",
                excerpt: "How to renew subscriptions, buy extra user seats, and check payment status.",
                content: `
                    <p class="mb-4">Manage subscription licenses to prevent checkout interruptions:</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">1. Access Billing Panel</h3>
                    <p class="mb-4">Navigate to the <strong>Subscription</strong> screen in your dashboard.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">2. Renew plans</h3>
                    <p class="mb-4">Review the plans matrix, select the appropriate tier (e.g. Starter, Premium Pro, Enterprise), and proceed to our payment gateway to complete authorization.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">3. Billing history</h3>
                    <p class="mb-4">Download official license invoices directly from your Subscription Log history.</p>
                `
            },
            {
                title: "Resetting passwords and configuring OTP security",
                readTime: "3 min read",
                excerpt: "Enable two-factor authentication, modify login emails, and update passwords.",
                content: `
                    <p class="mb-4">Maintain secure accounts to prevent leakage of client invoice records:</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">1. Edit profile settings</h3>
                    <p class="mb-4">Go to <strong>Profile Settings</strong> under settings.</p>
                    
                    <h3 class="text-lg font-bold text-gray-950 mt-6 mb-2">2. Reset password</h3>
                    <p class="mb-4">Enter your current password, type the new security code, and verify via standard OTP verification parameters sent to your registered phone.</p>
                `
            }
        ]
    };

    // Global search logic matching all articles
    const getSearchResults = () => {
        if (!searchQuery) return [];
        const results = [];
        Object.entries(articlesData).forEach(([catTitle, articles]) => {
            articles.forEach(art => {
                if (
                    art.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    art.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    art.content.toLowerCase().includes(searchQuery.toLowerCase())
                ) {
                    results.push({ ...art, category: catTitle });
                }
            });
        });
        return results;
    };

    const globalSearchResults = getSearchResults();

    return (
        <div className="min-h-screen bg-gray-50 text-gray-800 font-sans">
            <SEO
                title={selectedArticle ? selectedArticle.title : (selectedCategory ? selectedCategory : "Help Center")}
                description="Get support for Gio Billing. Find tutorial guides, FAQs, and hardware configuration instructions."
                url="/help-center"
            />

            {/* Banner Section */}
            <div className="bg-[#022c22] text-white py-16 px-6 md:px-12 relative overflow-hidden text-center">
                {/* Visual elements */}
                <div className="absolute top-0 left-0 w-84 h-84 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="max-w-3xl mx-auto space-y-6 relative z-10">
                    <div className="space-y-2">
                        <p className="text-xs font-bold text-emerald-400 uppercase tracking-widest">Support Portal</p>
                        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight font-outfit">Gio Billing Help Center</h1>
                        <p className="text-base text-emerald-100/70 max-w-xl mx-auto font-light">
                            Search for article guides, tutorials, and configurations to resolve any setup issues.
                        </p>
                    </div>

                    {/* Search Field */}
                    <div className="max-w-lg mx-auto relative mt-4 shadow-xl shadow-black/10">
                        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input 
                            type="text" 
                            placeholder="Search for articles, configurations, guides..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                // Reset page states when searching to see search results
                                if (e.target.value) {
                                    setSelectedCategory(null);
                                    setSelectedArticle(null);
                                }
                            }}
                            className="w-full bg-white text-gray-900 placeholder:text-gray-400 pl-12 pr-6 py-3.5 rounded-full outline-none focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-medium border border-transparent"
                        />
                    </div>
                </div>
            </div>

            {/* Main Interactive Content Area */}
            <div className="max-w-6xl mx-auto px-6 py-12 md:py-16">
                
                {/* 1. Global Search Results View */}
                {searchQuery && (
                    <div className="space-y-8">
                        <div className="flex items-center gap-3 justify-between pb-6 border-b border-gray-200">
                            <div>
                                <h2 className="text-2xl font-extrabold text-gray-950 tracking-tight font-outfit">
                                    Search Results
                                </h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Showing matches for "{searchQuery}"
                                </p>
                            </div>
                            <button 
                                onClick={() => setSearchQuery('')}
                                className="text-xs font-bold text-[#047857] hover:underline"
                            >
                                Clear Search
                            </button>
                        </div>

                        {globalSearchResults.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {globalSearchResults.map((art, idx) => (
                                    <div 
                                        key={idx}
                                        onClick={() => {
                                            setSelectedCategory(art.category);
                                            setSelectedArticle(art);
                                            setSearchQuery('');
                                        }}
                                        className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md cursor-pointer hover:scale-[1.01] active:scale-[0.99] transition-all group flex flex-col justify-between"
                                    >
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] uppercase font-bold tracking-widest bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full">
                                                    {art.category}
                                                </span>
                                                <span className="text-xs text-gray-400 font-medium">
                                                    {art.readTime}
                                                </span>
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-950 group-hover:text-emerald-750 transition-colors">
                                                {art.title}
                                            </h3>
                                            <p className="text-sm text-gray-500 leading-relaxed font-light">
                                                {art.excerpt}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-xs font-bold text-[#047857] group-hover:gap-2.5 transition-all mt-6">
                                            <span>Read Article</span>
                                            <ArrowRight size={12} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-20 text-gray-400">
                                <HelpCircle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                                <p className="text-base font-medium">No articles found matching your search term.</p>
                                <p className="text-sm text-gray-400 mt-1">Try searching for simple words like "GST", "POS", or "stock".</p>
                            </div>
                        )}
                    </div>
                )}

                {/* 2. Standard Category View */}
                {!searchQuery && !selectedCategory && !selectedArticle && (
                    <div>
                        <div className="mb-10 text-center md:text-left">
                            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-955 tracking-tight font-outfit">
                                Browse by Category
                            </h2>
                            <p className="text-sm text-gray-500 mt-1 font-light">
                                Select a module to view complete workflows and documentation.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {categories.map((cat, idx) => {
                                const IconComponent = cat.icon;
                                return (
                                    <div 
                                        key={idx}
                                        onClick={() => setSelectedCategory(cat.title)}
                                        className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:scale-[1.02] active:scale-[0.99] cursor-pointer transition-all duration-300 group flex flex-col justify-between"
                                    >
                                        <div className="space-y-4">
                                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${cat.color} shadow-sm group-hover:scale-110 transition-transform duration-300`}>
                                                <IconComponent size={24} />
                                            </div>
                                            <div className="space-y-2">
                                                <h3 className="text-lg font-bold text-gray-955 group-hover:text-emerald-700 transition-colors">
                                                    {cat.title}
                                                </h3>
                                                <p className="text-sm text-gray-500 leading-relaxed font-light">
                                                    {cat.desc}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 group-hover:gap-2.5 transition-all mt-6">
                                            <span>Read Articles</span>
                                            <ArrowRight size={14} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* 3. Category Article List View */}
                {!searchQuery && selectedCategory && !selectedArticle && (
                    <div className="space-y-8">
                        <button 
                            onClick={() => setSelectedCategory(null)}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-[#047857] hover:underline"
                        >
                            <ArrowLeft size={16} />
                            <span>Back to Categories</span>
                        </button>

                        <div>
                            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-955 tracking-tight font-outfit">
                                {selectedCategory}
                            </h2>
                            <p className="text-sm text-gray-500 mt-1 font-light">
                                Browse tutorials and setup configurations under this module.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {articlesData[selectedCategory]?.map((art, idx) => (
                                <div 
                                    key={idx}
                                    onClick={() => setSelectedArticle(art)}
                                    className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md cursor-pointer hover:scale-[1.01] active:scale-[0.99] transition-all group flex flex-col justify-between"
                                >
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
                                            <BookOpen size={14} />
                                            <span>{art.readTime}</span>
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-955 group-hover:text-emerald-700 transition-colors">
                                            {art.title}
                                        </h3>
                                        <p className="text-sm text-gray-500 leading-relaxed font-light">
                                            {art.excerpt}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 group-hover:gap-2.5 transition-all mt-6">
                                        <span>Open Tutorial</span>
                                        <ArrowRight size={14} />
                                    </div>
                                </div>
                            )) || (
                                <div className="col-span-2 text-center py-12 text-gray-400">
                                    No articles written yet for this category.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* 4. Article Reader View */}
                {!searchQuery && selectedArticle && (
                    <div className="max-w-3xl mx-auto space-y-8">
                        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                            <button 
                                onClick={() => setSelectedArticle(null)}
                                className="inline-flex items-center gap-2 text-sm font-semibold text-[#047857] hover:underline"
                            >
                                <ArrowLeft size={16} />
                                <span>Back to Articles</span>
                            </button>
                            <button 
                                onClick={() => {
                                    setSelectedCategory(null);
                                    setSelectedArticle(null);
                                }}
                                className="text-xs text-gray-500 hover:text-gray-900 transition-colors"
                            >
                                Help Desk Home
                            </button>
                        </div>

                        <article className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-10 space-y-6">
                            <div className="space-y-3">
                                <div className="flex items-center gap-3 text-xs text-emerald-700 font-semibold uppercase tracking-wider">
                                    <span>{selectedCategory}</span>
                                    <span className="text-gray-300 font-normal">|</span>
                                    <div className="flex items-center gap-1.5 text-gray-400 normal-case font-medium">
                                        <Clock size={12} />
                                        <span>{selectedArticle.readTime}</span>
                                    </div>
                                </div>
                                <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-955 tracking-tight font-outfit leading-tight">
                                    {selectedArticle.title}
                                </h1>
                            </div>

                            <div 
                                className="prose prose-slate max-w-none text-gray-700 leading-relaxed text-[16px] border-t border-gray-100 pt-6 font-light"
                                dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
                            />
                        </article>
                    </div>
                )}
            </div>

            {/* Direct Assistance Support Banner */}
            <div className="bg-[#022c22] text-white py-16 px-6 md:px-12 text-center relative overflow-hidden border-t border-emerald-900">
                <div className="absolute inset-0 opacity-5 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:16px_16px]" />
                
                <div className="max-w-xl mx-auto space-y-6 relative z-10">
                    <div className="w-12 h-12 bg-emerald-900 border border-emerald-800 rounded-2xl flex items-center justify-center text-emerald-400 mx-auto">
                        <MessageSquare size={22} />
                    </div>
                    <div className="space-y-2">
                        <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight font-outfit">Still looking for answers?</h2>
                        <p className="text-sm text-emerald-100/70 leading-relaxed max-w-sm mx-auto font-light">
                            Can't find the guide you need? Get in touch with our technology support team for help.
                        </p>
                    </div>
                    <div className="pt-2">
                        <Link 
                            to="/contact" 
                            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold text-sm transition-all active:scale-95 shadow-md shadow-emerald-950/20"
                        >
                            <span>Contact Support Desk</span>
                            <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HelpCenterPage;
